import React, { useEffect } from 'react';
import { Field, FieldArray, useFormikContext } from "formik";
import { FormControlLabel, Grid, IconButton, MenuItem, Checkbox, Box, Button, InputAdornment } from "@mui/material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircleOutline";
import FormikTextField from "../../../../components/common/formikFields/FormikTextField";
import FormikDatePicker from "../../../../components/common/formikFields/FormikDatePicker";
import { PRICE_PER_PACKAGE_TYPES } from '../../ratecard/RateCardConstants';
import DatePicker, { DateObject } from "react-multi-date-picker"; // Assuming you're using this library
import "react-datepicker/dist/react-datepicker.css"; 
import {
  isDisabledDate,
  isDisabledFieldDueToDateRangeEdits,
} from "../utils/RatecardUtils";// Import styles for datepicker

const CiCustomFields = (props: any) => {
  const { editData, values, setFieldValue, invoiceStartDate, invoiceEndDate } = props;

  useEffect(() => {
    if (values.ratecardCustomFieldsData && values.ratecardCustomFieldsData.length > 0) {
      setFieldValue('show_custom_fields', true);
    }
  }, [values.ratecardCustomFieldsData, setFieldValue]);

  const handleAmountSignToggle = (index: number) => {
    const currentValue = Number(values.ratecardCustomFieldsData[index].ratecardCustomFieldAmount) || 0;
    setFieldValue(
      `ratecardCustomFieldsData.${index}.ratecardCustomFieldAmount`,
      -currentValue
    );
  };

  const handleAmountChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const newValue = event.target.value === '' ? 0 : Number(event.target.value);
    setFieldValue(
      `ratecardCustomFieldsData.${index}.ratecardCustomFieldAmount`,
      newValue
    );
  };

  const disableFieldDateRanges = () => {
    // Add your logic for disabling the date range
    return false;
  };

  return (
    <Grid container alignItems="center">
      <Grid item xs={12} sm={4}>
        <FormControlLabel
          id="show_custom_fields"
          name="show_custom_fields"
          label="Custom Fields"
          labelPlacement="start"
          sx={{ mt: 2 }}
          control={
            <Field name="show_custom_fields">
              {({ field, form }: any) => (
                <Checkbox
                  {...field}
                  checked={field.value}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
                    form.setFieldValue(field.name, checked);
                    if (checked) {
                      if (!values.ratecardCustomFieldsData || values.ratecardCustomFieldsData.length === 0) {
                        form.setFieldValue('ratecardCustomFieldsData', [{
                          ratecardCustomFieldName: '',
                          ratecardCustomFieldAmount: 0,
                          ratecardCustemFieldPerPackage: '',
                          ratecardCustomFieldApplicableFor: '',
                          customFieldDate: ''
                        }]);
                      }
                    } else {
                      form.setFieldValue('ratecardCustomFieldsData', []);
                    }
                  }}
                />
              )}
            </Field>
          }
        />
      </Grid>

      {values.show_custom_fields && (
        <FieldArray
          name="ratecardCustomFieldsData"
          render={(arrayHelpers) => (
            <Grid container spacing={2} alignItems="center" sx={{ position: "relative", ml: -1, marginLeft: '20px' }}>
              <Box
                sx={{
                  position: 'absolute',
                  left: '20px',
                  top: 24,
                  bottom: 24,
                  width: '2px',
                  backgroundColor: '#4caf50',
                  zIndex: 0
                }}
              />

              {values.ratecardCustomFieldsData.map((customField: any, index: number) => (
                <Grid container item alignItems="center" spacing={1} key={index} sx={{ mb: 2 }}>
                  <Grid item sx={{ width: '40px', position: 'relative', zIndex: 1 }}>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        bgcolor: '#4caf50',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 14,
                        fontWeight: 'bold',
                        ml: 1
                      }}
                    >
                      {index + 1}
                    </Box>
                  </Grid>

                  <Grid item xs>
                    <Grid container spacing={2}>
                      <Grid item xs={3}>
                        <FormikTextField
                          variant="outlined"
                          name={`ratecardCustomFieldsData.${index}.ratecardCustomFieldName`}
                          size="small"
                          label="Field Name"
                          fullWidth
                          margin="normal"
                        />
                      </Grid>

                      <Grid item xs={3}>
                        <FormikTextField
                          variant="outlined"
                          name={`ratecardCustomFieldsData.${index}.ratecardCustomFieldAmount`}
                          size="small"
                          label="Price Per Package"
                          value={values.ratecardCustomFieldsData[index].ratecardCustomFieldAmount}
                          onChange={(e:any) => handleAmountChange(e, index)}
                          InputProps={{
                            startAdornment: (
                              <>
                                <Button
                                  onClick={() => handleAmountSignToggle(index)}
                                  sx={{
                                    minWidth: '32px',
                                    padding: '4px',
                                    marginRight: '4px',
                                    fontSize: '20px',
                                    fontWeight: 'bold',
                                    lineHeight: 1,
                                    cursor: 'pointer',
                                    color: '#4caf50'
                                  }}
                                >
                                  {Number(values.ratecardCustomFieldsData[index].ratecardCustomFieldAmount) >= 0 ? '+' : '-'}
                                </Button>
                                <InputAdornment position="start">$</InputAdornment>
                              </>
                            ),
                            inputProps: {
                              step: "0.01",
                              style: { textAlign: 'right' }
                            }
                          }}
                          type="number"
                          fullWidth
                          margin="normal"
                        />
                      </Grid>

                      <Grid item xs={2}>
                        <FormikTextField
                          select
                          margin="normal"
                          name={`ratecardCustomFieldsData.${index}.ratecardCustemFieldPerPackage`}
                          label="Price Per Package Type"
                          size="small"
                          fullWidth
                          variant="outlined"
                          required
                        >
                          {PRICE_PER_PACKAGE_TYPES.map((type: string, idx: number) => (
                            <MenuItem key={idx} value={type}>{type}</MenuItem>
                          ))}
                        </FormikTextField>
                      </Grid>

                      <Grid item>
                        {/* Date Picker */}
                        {/* <div style={{ display: "flex", alignItems: "center", marginLeft: "16px" }}>
                        <DatePicker
                        format="MM/DD/YYYY"
                        className="green"
                        dateSeparator=" ~ "
                        range
                        rangeHover
                        placeholder="Date Range"
                        style={{ height: "36px", marginTop: "8px" }}
                        minDate={props.invoiceStartDate}
                        maxDate={props.invoiceEndDate}
                        disabled={disableFieldDateRanges()}
                        
                        value={values.fuel_surcharge_date_range}
                        onChange={(value:any) => {
                          setFieldValue(`fuel_surcharge_date_range`, value);
                        }}
                            mapDays={({ date }: { date: DateObject }) => {
                              let localProps: any = {};
                              const usedDateRanges = values.custom_fields_date_range_values.map((item: any) => {
                                let startDate = typeof item.startDate === "string" ? new Date(item.startDate) : item.startDate;
                                let endDate = typeof item.endDate === "string" ? new Date(item.endDate) : item.endDate;
                                return [startDate, endDate];
                              });

                              if (isDisabledDate(date.toDate(), usedDateRanges)) {
                                localProps.disabled = true;
                                localProps.style = { color: "gray", pointerEvents: "none" };
                              }
                              return localProps;
                            }}
                          />
                          <IconButton
                            color="success"
                            style={{ marginLeft: "8px" }}
                            disabled={disableFieldDateRanges() || (values.custom_fields_date_range[index] && values.custom_fields_date_range[index].length < 2)}
                            onClick={() => {
                              if (values.custom_fields_date_range[index] && values.custom_fields_date_range[index].length === 2) {
                                let dateRangeValues = values.custom_fields_date_range_values;

                                let customDateRangeValue = {
                                  startDate: values.custom_fields_date_range[index][0],
                                  endDate: values.custom_fields_date_range[index][1],
                                };

                                // Add custom value logic here if needed
                                dateRangeValues.push(customDateRangeValue);
                                setFieldValue(`custom_fields_date_range_values`, dateRangeValues);
                              }
                            }}
                          >
                            <AddCircleIcon />
                          </IconButton>
                        </div> */}

                        <IconButton
                          sx={{
                            bgcolor: '#4caf50',
                            color: 'white',
                            '&:hover': { bgcolor: '#388e3c' },
                            mt: 2
                          }}
                          onClick={() => {
                            if (values.ratecardCustomFieldsData.length === 1) {
                              setFieldValue('show_custom_fields', false);
                            }
                            arrayHelpers.remove(index);
                          }}
                        >
                          <RemoveCircleIcon fontSize="small" />
                        </IconButton>

                        <IconButton
                          sx={{
                            bgcolor: '#4caf50',
                            color: 'white',
                            '&:hover': { bgcolor: '#388e3c' },
                            mt: 2,
                            ml: 1
                          }}
                          onClick={() => {
                            arrayHelpers.push({
                              ratecardCustomFieldName: "",
                              ratecardCustomFieldAmount: 0,
                              ratecardCustemFieldPerPackage: "",
                              customFieldDate: "",
                            });
                          }}
                        >
                          <AddCircleIcon fontSize="small" />
                        </IconButton>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          )}
        />
      )}
    </Grid>
  );
};

export default CiCustomFields;
